/**
 * Toast — a notification system on Radix Toast. Mount <ToastProvider> once near
 * the app root, then call the imperative helper from `useToast()`:
 *
 *   const toast = useToast();
 *   toast.success("已保存", "这一章的改动已经同步。");
 *   toast.error("发布失败", "网络开小差了，稍后再试。");
 *   toast.info("草稿已恢复");
 *
 * Three variants (success / error / info) with a coloured left rail + icon, a
 * title, optional description, and a close button. Auto-dismisses after
 * `duration` ms (default 4500). Swipe-to-dismiss to the right.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import * as RadixToast from "@radix-ui/react-toast";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";
import { IconButton } from "./IconButton.js";

export type ToastVariant = "success" | "error" | "info";

export interface ToastOptions {
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly variant?: ToastVariant;
  /** Auto-dismiss after ms. @default 4500 */
  readonly duration?: number;
}

interface ToastEntry extends ToastOptions {
  readonly id: number;
}

export interface ToastApi {
  /** Show a toast with explicit options. Returns its id. */
  show: (opts: ToastOptions) => number;
  success: (title: ReactNode, description?: ReactNode) => number;
  error: (title: ReactNode, description?: ReactNode) => number;
  info: (title: ReactNode, description?: ReactNode) => number;
  /** Dismiss a specific toast. */
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const ICONS = {
  success: CheckCircle2,
  error: AlertCircle,
  info: Info,
} as const;

export interface ToastProviderProps {
  readonly children: ReactNode;
  /** Default auto-dismiss duration (ms). @default 4500 */
  readonly duration?: number;
}

export function ToastProvider({ children, duration = 4500 }: ToastProviderProps) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const show = useCallback((opts: ToastOptions) => {
    const id = ++idRef.current;
    setToasts((prev) => [...prev, { ...opts, id }]);
    return id;
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      dismiss,
      success: (title, description) => show({ title, description, variant: "success" }),
      error: (title, description) => show({ title, description, variant: "error" }),
      info: (title, description) => show({ title, description, variant: "info" }),
    }),
    [show, dismiss],
  );

  return (
    <ToastContext.Provider value={api}>
      <RadixToast.Provider duration={duration} swipeDirection="right">
        {children}
        {toasts.map((t) => {
          const variant = t.variant ?? "info";
          const Icon = ICONS[variant];
          return (
            <RadixToast.Root
              key={t.id}
              className={`lc-toast lc-toast--${variant}`}
              duration={t.duration ?? duration}
              onOpenChange={(open) => {
                if (!open) dismiss(t.id);
              }}
            >
              <span className="lc-toast__icon" aria-hidden="true">
                <Icon size={18} />
              </span>
              <div className="lc-toast__main">
                <RadixToast.Title className="lc-toast__title">{t.title}</RadixToast.Title>
                {t.description != null ? (
                  <RadixToast.Description className="lc-toast__description">
                    {t.description}
                  </RadixToast.Description>
                ) : null}
              </div>
              <RadixToast.Close asChild aria-label="关闭通知">
                <span className="lc-toast__close">
                  <IconButton label="关闭通知" size="sm">
                    <X size={15} aria-hidden="true" />
                  </IconButton>
                </span>
              </RadixToast.Close>
            </RadixToast.Root>
          );
        })}
        <RadixToast.Viewport className="lc-toast-viewport" />
      </RadixToast.Provider>
    </ToastContext.Provider>
  );
}

/** Access the imperative toast API. Must be used under <ToastProvider>. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (ctx == null) {
    throw new Error("useToast must be used within a <ToastProvider>");
  }
  return ctx;
}
